/**
 * Cross-package display formatters. Lives in `@prospect/shared` so the
 * SPA, the orchestrator, and the email package all share one source of
 * truth for human-facing job-id rendering.
 */

/**
 * Pad an integer job number to **at least** 8 digits with leading zeros
 * — the canonical wire format for friendly job ids. Used by:
 *
 * - the admin UI's `/jobs/<n>` URL (`apps/admin-ui/src/routes/JobList.tsx`,
 *   `JobDetail.tsx`),
 * - the cost-alert email body (`packages/email/src/templates/cost-alert.ts`),
 * - the orchestrator's `buildReviewUrl` / `buildJobUrl` deep-link builders
 *   (`services/orchestrator/src/lib/cost-alert.ts`,
 *   `pending-review.ts`, `review-sweeper.ts`).
 *
 * Width is **at least** 8 digits, not exactly 8: `String.padStart` is a
 * no-op once the value already has ≥8 digits, so the format spills
 * gracefully past the 99,999,999 ceiling without breaking. Mirrors
 * Salesforce's "spill to 9 digits at 100M" posture — operators stop
 * relying on the fixed-width column ordering once the prefix grows,
 * but no consumer crashes. The orchestrator's resolver
 * (`resolveJobIdentifier`) already accepts arbitrary-length numerics,
 * so the URL surface keeps working end-to-end.
 *
 * The `#` prefix is *display-only* and added at call sites — URLs use
 * the unprefixed form (`/jobs/00000001`). Width chosen at 8 digits per
 * the locked decision in issue #85; see the wiki at
 * `../../../docs/decisions/2026-05-09-job-auto-number.md`.
 */
export function formatJobNumber(n: number): string {
  return String(n).padStart(8, "0");
}

/**
 * First 8 hex characters of a UUID — a human-readable disambiguator
 * for email subjects, queue rows, and admin-ui labels where the full
 * UUID is overkill. Mirrors `formatJobNumber`'s purpose for entities
 * without a friendly auto-incrementing handle.
 *
 * Collisions at 8 hex chars are at ~256B rows (16⁸ ≈ 4.3B namespace
 * with birthday-attack √4.3B ≈ 66k for 50% collision risk), more than
 * sufficient for human disambiguation of pending presentations in
 * the M2 admin-ui surfaces (issue #144). Authoritative identity
 * always travels as the full UUID — never use this for storage,
 * routing, or any equality check.
 */
export function shortenUuid(uuid: string): string {
  return uuid.replace(/-/g, "").slice(0, 8);
}
